/**
 * Ad size configuration
 */
export const ALLOWED_AD_SIZES = [
  { width: 300, height: 250 },
  { width: 320, height: 50 },
  { width: 320, height: 100 },
] as const;

export type Size = (typeof ALLOWED_AD_SIZES)[number];

/**
 * Ad format type - native (text+image) or display (image-only)
 */
export type AdFormat = 'native' | 'display';

/**
 * Refresh configuration returned by backend
 * Controls refreshable ad behavior dynamically
 */
export interface RefreshConfig {
  /** Interval between ad refreshes in milliseconds */
  intervalMs: number;
  /** Maximum impressions allowed per session window */
  maxImpressionsPerSession: number;
  /** Duration of the session window in milliseconds */
  sessionWindowMs: number;
}

/**
 * Ad placement types - predefined or custom string
 */
export type Placement = 'internal' | 'popup' | 'sidepanel' | 'content_script';

/**
 * Options for requesting an ad
 */
export interface AdOptions {
  /** Placement identifier for the ad slot */
  placement: Placement;
  /** Dimensions of the ad */
  size: Size;
  /** Optional content categories for targeting */
  categories?: string[];
  /** Additional custom parameters */
  [key: string]: any;
}

/**
 * Result returned from createAd()
 */
export interface AdResult {
  /** Ready-to-append div element containing ad HTML */
  element: HTMLDivElement;
  /** Unique impression ID for tracking */
  impressionId: string;
  /** Optional click-through URL */
  clickUrl?: string;
}

/**
 * SDK initialization configuration
 */
export interface SDKConfig {
  /** Your PlayaYield API key */
  apiKey: string;
  /** Base API URL (defaults to https://api.yourplatform.com) */
  baseUrl?: string;
  /**
   * Enable verbose SDK logging to the console.
   * Defaults to false. Set to true during development to see detailed logs.
   *
   * You can also toggle logging at runtime without changing code by running
   * this in the extension's DevTools console:
   *   localStorage.setItem('playayield:debug', 'true')
   */
  debug?: boolean;
}

/**
 * Ad creative response from backend
 * Contains structured data for building ad elements - never raw HTML
 */
export interface AdCreative {
  /** Unique identifier for tracking this impression */
  impressionId: string;
  /** Click-through URL for the ad */
  clickUrl: string;
  /** Ad dimensions */
  width: number;
  height: number;
  /** URL to the ad image */
  imageUrl: string;
  /** Alt text for the image (accessibility) */
  imageAlt?: string;
  /** Optional headline text */
  title?: string;
  /** Optional description text */
  description?: string;
  /** Optional call-to-action text */
  ctaText?: string;
  /** Optional advertiser/sponsor name */
  advertiser?: string;
  /** Provider name (e.g. 'mgid', 'revcontent') */
  provider?: string;
  /** Optional background color (hex or CSS color) */
  backgroundColor?: string;
  /** Tracking hash for view events */
  viewHash?: string;
  /** Tracking hash for impression events */
  impressionHash?: string;
  /** Tracking hash for available events (Taboola) */
  availableHash?: string;
  /** URL to the Ad Choices icon hosted on the backend */
  adChoicesUrl?: string;
  /** Refresh configuration from backend - controls refreshable ad behavior */
  refreshConfig?: RefreshConfig;
  /** Ad format: 'native' (text+image) or 'display' (image-only). Defaults to 'native' if omitted. */
  format?: AdFormat;
}

/**
 * Tracking event types supported by the SDK
 */
export enum TrackingEventType {
  IMPRESSION = 'impression',
  CLICK = 'click',
}

/**
 * Event tracking payload
 */
export interface TrackingEvent {
  type: TrackingEventType;
  impressionId: string;
  timestamp: number;
  metadata?: Record<string, any>;
}

/**
 * Message types for chrome.runtime communication
 */
export enum MessageType {
  AD_REQUEST = 'AD_REQUEST',
  TRACK_EVENT = 'TRACK_EVENT',
  TRACK_AVAILABLE = 'TRACK_AVAILABLE',
  GET_CONFIG = 'GET_CONFIG',
  PING = 'PING',
  GET_SESSION_DATA = 'GET_SESSION_DATA',
  UPDATE_SESSION_IMPRESSION = 'UPDATE_SESSION_IMPRESSION',
  STOP_REFRESHABLE_AD = 'STOP_REFRESHABLE_AD',
}

/**
 * Message structure for background communication
 */
export interface BackgroundMessage {
  type: MessageType;
  payload: any;
}

/**
 * Response from background handler
 */
export interface BackgroundResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Custom error types
 */
export interface ManagedRefreshableAdOptions extends AdOptions {
  /** Container element or CSS selector where ad should be appended */
  container: HTMLElement | string;
}

/**
 * Session data stored in chrome.storage.session
 */
export interface SessionData {
  sessionId: string;
  startTime: number;
  windowStartTime: number;
  impressionCount: number;
  activeRefreshableAds: string[];
}

/**
 * Result returned from createManagedRefreshableAd()
 */
export interface ManagedRefreshableAdResult {
  /** Stop refreshing and cleanup */
  stop: () => void;
  /** Check if still active */
  isActive: () => boolean;
}

export class PlayaYieldError extends Error {
  constructor(
    message: string,
    public code: string,
    public details?: any
  ) {
    super(message);
    this.name = 'PlayaYieldError';
  }
}

export class NetworkError extends PlayaYieldError {
  constructor(message: string, details?: any) {
    super(message, 'NETWORK_ERROR', details);
    this.name = 'NetworkError';
  }
}

export class ConfigError extends PlayaYieldError {
  constructor(message: string) {
    super(message, 'CONFIG_ERROR');
    this.name = 'ConfigError';
  }
}
